import type {
  ChannelSources,
  CompetitionAdvertising,
  CompetitionDetail,
  CompetitionDevices,
  CompetitionFunnel,
  CompetitionFunnelStage,
  CompetitionSources,
  DetailFrame,
  SalesGranularity,
  SalesPoint,
} from '@/api/competitionDetail'
import type { CompetitionRow } from '@/api/competitions'
import { FUNNEL_STAGE_LABELS, FUNNEL_STAGES, type AttributionModel, type FunnelStageKey, type PeriodInfo } from '@/api/types'
import { minorUnitExponent } from '@/design/format'
import { ApiError } from '@/lib/api'
import { addDays, dayCount, eachDay, resolvePeriod, type GlobalFilters } from '@/lib/filters'
import type { DemoDataset } from '@/mock/generators/engine'
import { DEMO_TIMEZONE, scopeFor } from '@/mock/generators/engine'

import { MockCompetitionsStore } from '../competitions/mock'

/**
 * The demo tenant's competition page: built on the demo competitions list's row for the
 * same competition and period (`../competitions/mock`), so the summary is the list's
 * row, and from the same demo engine's days, so the sales series sums to it. Its
 * schedule follows the README's rules (velocity over the last seven complete days on
 * sale, pacing against a straight line); the funnel, devices, sources and customers
 * split the row's own figures; spend, CPA and ROAS are never given per competition, as
 * in production. A competition the demo does not have answers the API's 404.
 */

const NOT_FOUND = 'no such competition in this tenant'

const COVERAGE_NOTE =
  'Views, selector opens, tickets selected, add-to-baskets and conversion are counted from measured sessions only: visitors who allowed analytics. Demo data.'

/** A fraction to four places, as the API rounds one. */
function ratio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? Math.round((numerator / denominator) * 10_000) / 10_000 : null
}

/** Whole `total` split by `weights` (largest remainder), so the parts always sum to it. */
function split(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((acc, weight) => acc + weight, 0)
  const exact = weights.map((weight) => (sum > 0 ? (total * weight) / sum : 0))
  const parts = exact.map(Math.floor)
  let left = total - parts.reduce((acc, part) => acc + part, 0)
  const order = exact.map((value, index) => ({ index, rest: value - Math.floor(value) })).sort((a, b) => b.rest - a.rest)
  for (const { index } of order) {
    if (left <= 0) break
    parts[index] = (parts[index] ?? 0) + 1
    left -= 1
  }
  return parts
}

function dayOf(instant: string | null): string | null {
  return instant ? instant.slice(0, 10) : null
}

/** The sources each channel's demo orders are credited to, under every model. */
const DEMO_CHANNELS: readonly { channel: string | null; label: string; source: string | null; medium: string | null; campaign: string | null }[] = [
  { channel: 'paid_social', label: 'Paid social', source: 'facebook', medium: 'paid_social', campaign: 'launch' },
  { channel: 'organic_search', label: 'Organic search', source: 'google', medium: 'organic', campaign: null },
  { channel: 'email', label: 'Email', source: 'newsletter', medium: 'email', campaign: 'weekly' },
  { channel: 'direct', label: 'Direct', source: null, medium: null, campaign: null },
  { channel: null, label: 'Unattributed', source: null, medium: null, campaign: null },
]

/** How each model shares the orders out: last non-direct credits direct to the touch before it; paid click moves a share to the paid click before that. */
const MODEL_WEIGHTS: Record<AttributionModel, readonly number[]> = {
  first_touch: [5, 3, 1, 2, 1],
  last_touch: [3, 2, 2, 4, 1],
  last_non_direct: [4, 2, 3, 0, 1],
  paid_click: [5, 2, 2, 0, 1],
}

export class MockCompetitionDetailStore {
  private readonly unit: number
  /** The demo list the summary is read from, over the same dataset, whose days it caches. */
  private readonly list: MockCompetitionsStore

  constructor(
    private readonly dataset: DemoDataset,
    private readonly currency: string,
  ) {
    this.unit = 10 ** minorUnitExponent(currency)
    this.list = new MockCompetitionsStore(dataset, currency)
  }

  private get frame(): DetailFrame {
    return { currency: this.currency, timezone: DEMO_TIMEZONE }
  }

  /** The list's row for the competition, or the API's 404. */
  private located(filters: GlobalFilters, competitionId: string): { row: CompetitionRow; period: PeriodInfo } {
    const found = this.list.find(filters, competitionId)
    if (!found) throw new ApiError(404, 'tenant_scope_error', NOT_FOUND)
    return found
  }

  detail(filters: GlobalFilters, competitionId: string, granularity: SalesGranularity = 'day'): { data: CompetitionDetail; period: PeriodInfo } {
    const { row, period } = this.located(filters, competitionId)
    const days = this.days(filters, row)
    const series = granularity === 'week' ? weeks(days, period.from, period.to, this.unit) : days
    const schedule = this.schedule(row, days)
    const notAvailable: CompetitionDetail['notAvailable'] = {
      summary: {
        adSpendMinor: 'needs_ad_platform',
        attributedRevenueMinor: 'needs_ad_platform',
        cpaMinor: 'needs_ad_platform',
        roas: 'needs_ad_platform',
        ...(row.revenueNetMinor === null ? { revenueNetMinor: 'refund_not_itemised' as const } : {}),
      },
      ...(schedule.reasons.velocity ? { velocityPerDay: schedule.reasons.velocity } : {}),
      ...(schedule.reasons.pacing ? { pacing: schedule.reasons.pacing } : {}),
      priceChanges: 'no_price_history',
    }
    const guestOrders = row.orders - row.buyers
    const [guestMinor = 0, newMinor = 0, returningMinor = 0] = split(row.revenueGrossMinor, [
      guestOrders,
      row.newCustomers,
      row.returningCustomers,
    ])
    const touched = split(row.buyers, [5, 3, 2, 1])
    const firstTouchChannels = DEMO_CHANNELS.filter((channel) => channel.channel !== 'direct')
      .map((channel, index) => ({ channel: channel.channel, label: channel.label, customers: touched[index] ?? 0 }))
      .filter((entry) => entry.customers > 0)
    return {
      period,
      data: {
        ...this.frame,
        summary: row,
        schedule: schedule.figures,
        sales: {
          granularity,
          series,
          totals: { revenue: row.revenueGrossMinor / this.unit, tickets: row.ticketsSoldInRange, orders: row.orders },
        },
        customers: {
          newCustomers: row.newCustomers,
          returningCustomers: row.returningCustomers,
          guestOrders,
          revenue: { new: newMinor / this.unit, returning: returningMinor / this.unit, guest: guestMinor / this.unit },
          firstTouchChannels,
        },
        timeline: this.timeline(row, period),
        notAvailable,
        coverage: { firstEventAt: `${period.from}T00:00:00Z`, measuredFrom: `${period.from}T00:00:00Z`, note: COVERAGE_NOTE },
      },
    }
  }

  funnel(filters: GlobalFilters, competitionId: string): { data: CompetitionFunnel; period: PeriodInfo } {
    const { row, period } = this.located(filters, competitionId)
    const viewed = row.views ?? 0
    const sessions: Record<Exclude<FunnelStageKey, 'visitors'>, number> = {
      competition_viewed: viewed,
      ticket_selector_used: row.selectorOpens ?? 0,
      add_to_basket: row.addToBaskets ?? 0,
      checkout_started: Math.round(row.orders * 1.2),
      payment_attempted: Math.round(row.orders * 1.08),
      purchase_completed: row.orders,
    }
    const stages = FUNNEL_STAGES.map((key, index): CompetitionFunnelStage => {
      const base = { key, label: FUNNEL_STAGE_LABELS[key], withoutEvents: false }
      if (key === 'visitors') {
        return {
          ...base,
          users: null,
          sessions: null,
          stageConversionRate: null,
          totalConversionRate: null,
          dropOff: null,
          dropOffRate: null,
          unavailable: 'not_per_competition',
        }
      }
      const here = sessions[key]
      const aboveKey = FUNNEL_STAGES[index - 1]
      const above = aboveKey && aboveKey !== 'visitors' ? sessions[aboveKey] : null
      return {
        ...base,
        users: Math.round(here * 0.82),
        sessions: here,
        stageConversionRate: above === null ? null : ratio(here, above),
        totalConversionRate: above === null ? null : ratio(here, viewed),
        dropOff: above === null ? null : above - here,
        dropOffRate: above === null ? null : ratio(above - here, above),
        unavailable: null,
      }
    })
    return {
      period,
      data: {
        ...this.frame,
        stages,
        coverage: { firstEventAt: `${period.from}T00:00:00Z`, measuredFrom: `${period.from}T00:00:00Z`, note: COVERAGE_NOTE },
      },
    }
  }

  sources(filters: GlobalFilters, competitionId: string, model: AttributionModel): { data: CompetitionSources; period: PeriodInfo } {
    const { row, period } = this.located(filters, competitionId)
    const weights = MODEL_WEIGHTS[model]
    const orders = split(row.orders, weights)
    const tickets = split(row.ticketsSoldInRange, weights)
    const revenue = split(row.revenueGrossMinor, weights)
    const channels: ChannelSources[] = DEMO_CHANNELS.flatMap((channel, index) => {
      const figures = { orders: orders[index] ?? 0, tickets: tickets[index] ?? 0, revenue: (revenue[index] ?? 0) / this.unit }
      if (figures.orders === 0) return []
      return [
        {
          ...figures,
          channel: channel.channel,
          label: channel.label,
          revenueShare: ratio(revenue[index] ?? 0, row.revenueGrossMinor),
          sources: [{ ...figures, source: channel.source, medium: channel.medium, campaign: channel.campaign }],
        },
      ]
    })
    // Most revenue first, then most orders, then by label; the unattributed row last.
    channels.sort(
      (a, b) =>
        Number(a.channel === null) - Number(b.channel === null) ||
        b.revenue - a.revenue ||
        b.orders - a.orders ||
        a.label.localeCompare(b.label),
    )
    return {
      period,
      data: {
        ...this.frame,
        model,
        channels,
        totals: { orders: row.orders, tickets: row.ticketsSoldInRange, revenue: row.revenueGrossMinor / this.unit },
      },
    }
  }

  devices(filters: GlobalFilters, competitionId: string): { data: CompetitionDevices; period: PeriodInfo } {
    const { row, period } = this.located(filters, competitionId)
    const names = ['desktop', 'mobile', 'tablet', 'other', 'unknown']
    const viewing = split(row.views ?? 0, [40, 52, 6, 0, 2])
    const purchasing = split(row.orders, [44, 50, 5, 0, 1])
    const devices = names.map((device, index) => {
      const viewed = viewing[index] ?? 0
      // A purchase with no view of the competition in its session is not a conversion.
      const converted = device === 'unknown' ? 0 : Math.min(viewed, purchasing[index] ?? 0)
      return {
        device,
        viewingSessions: viewed,
        purchasingSessions: purchasing[index] ?? 0,
        convertedSessions: converted,
        conversionRate: ratio(converted, viewed),
      }
    })
    return {
      period,
      data: {
        ...this.frame,
        devices,
        botSessions: Math.round((row.views ?? 0) * 0.02),
        notAvailable: {},
        coverage: { firstEventAt: `${period.from}T00:00:00Z`, measuredFrom: `${period.from}T00:00:00Z`, note: COVERAGE_NOTE },
      },
    }
  }

  advertising(filters: GlobalFilters, competitionId: string): { data: CompetitionAdvertising; period: PeriodInfo } {
    const { row, period } = this.located(filters, competitionId)
    const launch = this.launchCampaign(row)
    const overlaps = launch !== null && (dayOf(launch.startsAt) ?? '') <= period.to && (dayOf(launch.endsAt) ?? '9999') >= period.from
    return {
      period,
      data: {
        ...this.frame,
        campaigns: overlaps
          ? [
              {
                ...launch,
                status: launch.campaignStatus,
                objective: 'sales',
                competitionsPromoted: 1,
                channels: [
                  { channelId: `chn_${row.competitionId}_meta`, name: 'Meta', channelType: 'meta_ads', kind: 'digital', status: 'active', startsAt: null, endsAt: null },
                  { channelId: `chn_${row.competitionId}_email`, name: 'Newsletter', channelType: 'email', kind: 'digital', status: 'active', startsAt: null, endsAt: null },
                ],
              },
            ]
          : [],
        adSpend: null,
        attributedRevenue: null,
        cpa: null,
        roas: null,
        notAvailable: {
          adSpend: 'no_spend_per_competition',
          attributedRevenue: 'no_spend_per_competition',
          cpa: 'no_spend_per_competition',
          roas: 'no_spend_per_competition',
        },
      },
    }
  }

  // --- the pieces ----------------------------------------------------------

  /** The competition's sales per day of the period, from the engine's days, summing to the row's. */
  private days(filters: GlobalFilters, row: CompetitionRow): SalesPoint[] {
    const resolved = resolvePeriod(filters, this.dataset.now)
    const scope = scopeFor(this.dataset.tenant, { ...filters, dimensions: { ...filters.dimensions, competition: undefined } })
    const byDay = new Map(this.dataset.period(resolved.current, scope).days.map((day) => [day.date, day.competitions.get(row.competitionId)]))
    const dates = eachDay(resolved.current)
    const weightsOf = (pick: (sold: { tickets: number; revenue: number; orders: number }) => number) =>
      dates.map((date) => {
        const sold = byDay.get(date)
        return sold ? pick(sold) : 0
      })
    const revenue = split(row.revenueGrossMinor, weightsOf((sold) => sold.revenue))
    const tickets = split(row.ticketsSoldInRange, weightsOf((sold) => sold.tickets))
    const orders = split(row.orders, weightsOf((sold) => sold.orders))
    return dates.map((date, index) => ({
      date,
      endDate: date,
      revenue: (revenue[index] ?? 0) / this.unit,
      tickets: tickets[index] ?? 0,
      orders: orders[index] ?? 0,
    }))
  }

  private schedule(row: CompetitionRow, days: readonly SalesPoint[]) {
    const { today, now } = this.dataset
    const start = dayOf(row.startAt)
    const end = dayOf(row.endAt)
    const draw = dayOf(row.drawAt)
    const yesterday = addDays(today, -1)
    const onSale = days.filter((day) => day.date <= yesterday && (!start || day.date >= start) && (!end || day.date <= end)).slice(-7)
    const first = onSale[0]
    const last = onSale[onSale.length - 1]
    const velocity =
      first && last
        ? Math.round((onSale.reduce((sum, day) => sum + day.tickets, 0) / onSale.length) * 100) / 100
        : null

    let pacing: CompetitionDetail['schedule']['pacing'] = null
    let expected: number | null = null
    let pacingReason: 'no_capacity' | 'no_schedule' | 'not_on_sale' | undefined
    const capacity = row.totalTicketCapacity
    if (!capacity || row.ticketsSoldLifetime === null) pacingReason = 'no_capacity'
    else if (!row.startAt || !row.endAt) pacingReason = 'no_schedule'
    else {
      const opens = Date.parse(row.startAt)
      const closes = Date.parse(row.endAt)
      const at = now.getTime()
      if (at < opens || at > closes) pacingReason = 'not_on_sale'
      else {
        const line = (capacity * (at - opens)) / (closes - opens)
        expected = Math.round(line)
        const sold = row.ticketsSoldLifetime
        pacing = sold >= line * 1.1 ? 'ahead' : sold < line * 0.9 ? 'behind' : 'on_track'
      }
    }

    return {
      figures: {
        daysToEnd: end ? dayCount(today, end) - 1 : null,
        daysToDraw: draw ? dayCount(today, draw) - 1 : null,
        velocityPerDay: velocity,
        velocityFrom: first?.date ?? null,
        velocityTo: last?.date ?? null,
        velocityDays: onSale.length,
        pacing,
        pacingExpectedSold: expected,
      },
      reasons: { velocity: velocity === null ? ('not_on_sale_in_range' as const) : undefined, pacing: pacingReason },
    }
  }

  /** The demo's one campaign per competition: launched the day after it opens, to its close. */
  private launchCampaign(row: CompetitionRow) {
    const start = dayOf(row.startAt)
    if (!start) return null
    return {
      campaignId: `cmpgn_${row.competitionId}`,
      campaignName: `${row.name} launch`,
      name: `${row.name} launch`,
      campaignStatus: row.active ? 'active' : 'completed',
      startsAt: `${addDays(start, 1)}T08:00:00Z`,
      endsAt: row.endAt,
    }
  }

  private timeline(row: CompetitionRow, period: PeriodInfo): CompetitionDetail['timeline'] {
    const inRange = (instant: string | null) => {
      const day = dayOf(instant)
      return day !== null && day >= period.from && day <= period.to
    }
    const launch = this.launchCampaign(row)
    const campaign = launch
      ? { campaignId: launch.campaignId, campaignName: launch.campaignName, campaignStatus: launch.campaignStatus }
      : null
    const none = { status: null, campaignId: null, campaignName: null, campaignStatus: null }
    const candidates = [
      { at: row.startAt, entry: { ...none, kind: 'status_changed' as const, status: 'live' } },
      { at: row.startAt, entry: { ...none, kind: 'started' as const } },
      { at: launch?.startsAt ?? null, entry: { ...none, ...campaign, kind: 'campaign_started' as const } },
      { at: launch?.endsAt ?? null, entry: { ...none, ...campaign, kind: 'campaign_ended' as const } },
      { at: row.endAt, entry: { ...none, kind: 'ended' as const } },
      { at: row.drawAt, entry: { ...none, kind: 'draw' as const } },
    ]
    const entries = candidates.flatMap(({ at, entry }) =>
      at !== null && inRange(at) && Date.parse(at) <= this.dataset.now.getTime() ? [{ ...entry, at }] : [],
    )
    entries.sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    const start = dayOf(row.startAt)
    return { statusAtStart: start !== null && start <= period.from ? 'live' : null, entries }
  }
}

/** Seven-day buckets counted from `from`, the last cut at `to`, each the sum of its days. */
function weeks(days: readonly SalesPoint[], from: string, to: string, unit: number): SalesPoint[] {
  const buckets: SalesPoint[] = []
  for (let start = from; start <= to; start = addDays(start, 7)) {
    const last = addDays(start, 6)
    const end = last > to ? to : last
    const inside = days.filter((day) => day.date >= start && day.date <= end)
    buckets.push({
      date: start,
      endDate: end,
      revenue: Math.round(inside.reduce((sum, day) => sum + day.revenue, 0) * unit) / unit,
      tickets: inside.reduce((sum, day) => sum + day.tickets, 0),
      orders: inside.reduce((sum, day) => sum + day.orders, 0),
    })
  }
  return buckets
}
